'use client';
import Avatar from '@/components/Avatar';
import CloseButtonMobile from '@/components/CloseButtonMobile';
import LoadingIndicator from '@/components/LoadingIndicator';
import WorkspaceSwitcher from '@/components/Molecules/WorkspaceSwitcher';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { PATH } from '@/constants';
import { useWorkspace } from '@/contexts/WorkspaceProvider';
import { useWorkspaceDetection } from '@/hooks/useWorkspaceDetection';
import { useAuth } from '@/contexts/AuthProvider';
import { getProfileAvatar } from '@/services/bots';
import { logout } from '@/services';
import { User } from '@supabase/supabase-js';
import CreditBalance from '@/components/CreditBalance';
import AnnouncementsBell from '@/components/announcements/AnnouncementsBell';
import { useCommandPalette } from '@/components/command-palette/CommandPaletteProvider';

// Extended user type with database fields
interface ExtendedUser extends User {
  first_name?: string;
  last_name?: string;
}
import clsx from 'clsx';
import Image from 'next/image';
import Link from 'next/link';
import { FC, useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronLeft, CircleHelp, Keyboard, LogOut, PanelLeftClose, PanelLeftOpen, Search, Settings } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import '../navigation.css';
import { usePathname } from 'next/navigation';
import Menu from '../Menu';
import SettingsSidebarMenu from '../Menu/SettingsSidebarMenu';
import { useProtectedLayoutContext } from '../context/ProtectedLayoutProvider';
import SidebarCreditUsage from './SidebarCreditUsage';

interface ISidebarProps {
  onCloseSidebar?: () => void;
}

interface IUserOptionsProps {
  avatarUrl: string;
  user?: ExtendedUser;
  handleLogout: () => void;
}

// Modern floating UserOptions component (exported for ProtectedLayout)
export const UserOptions = ({ avatarUrl, user, handleLogout }: IUserOptionsProps) => {
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const workspaceContext = useWorkspace();
  const { setOpen: setCommandPaletteOpen } = useCommandPalette();

  return (
    <div className='flex items-center'>
      <DropdownMenu open={accountMenuOpen} onOpenChange={setAccountMenuOpen}>
        <DropdownMenuTrigger asChild>
          <button
            type='button'
            aria-label='Account menu'
            className='flex items-center gap-[7px] rounded-full p-[3px] hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors group'
          >
            <span className='inline-block rounded-full'>
              <Avatar
                className='h-[34px] w-[34px]'
                src={
                  avatarUrl == '' || avatarUrl == null
                    ? '/images/user/avatar-default.png'
                    : avatarUrl
                }
                width={34}
                height={34}
              />
            </span>
            <ChevronDown className='h-3.5 w-3.5 text-slate-500 transition-colors group-hover:text-slate-700' />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className='w-[264px] overflow-visible rounded-xl border-slate-200 p-0 pb-1.5 shadow-[0_10px_25px_-5px_rgba(15,23,42,0.12)]'
          align='end'
          sideOffset={8}
        >
          <div className='flex items-center gap-2.5 px-3.5 pb-2 pt-3.5'>
            <span className='inline-block rounded-full'>
              <Avatar
                className='h-8 w-8'
                src={
                  avatarUrl == '' || avatarUrl == null
                    ? '/images/user/avatar-default.png'
                    : avatarUrl
                }
                width={40}
                height={40}
              />
            </span>
            <div className='flex-1 min-w-0'>
              <p className='truncate text-[13px] font-semibold text-slate-900'>
                {user?.user_metadata?.full_name ||
                  (user?.first_name && user?.last_name
                    ? `${user.first_name} ${user.last_name}`
                    : 'User')}
              </p>
              <p className='truncate text-xs text-slate-500'>{user?.email || user?.phone}</p>
            </div>
          </div>

          {workspaceContext?.hasWorkspaces && !workspaceContext.isMarketingOnly && (
            <div className='flex items-center justify-between border-b border-slate-100 px-3.5 pb-2.5'>
              <CreditBalance compact statusOnly showPlanSuffix className='!gap-0 !p-0' />
              <DropdownMenuItem
                asChild
                className='h-auto cursor-pointer p-0 text-xs font-medium text-blue-600 focus:text-blue-700'
              >
                <Link href='/settings?tab=billing-and-usage'>Manage</Link>
              </DropdownMenuItem>
            </div>
          )}

          <AnnouncementsBell variant='menu' onCta={() => setAccountMenuOpen(false)} />

          <DropdownMenuItem
            className='cursor-pointer gap-2.5 rounded-none px-3.5 py-2.5 text-[13px] text-slate-700 focus:bg-slate-50 focus:text-slate-900'
            onSelect={() => setCommandPaletteOpen(true)}
          >
            <CircleHelp className='h-[15px] w-[15px] text-slate-500' />
            Help &amp; support
          </DropdownMenuItem>

          <DropdownMenuItem
            asChild
            className='cursor-pointer gap-2.5 rounded-none border-b border-slate-100 px-3.5 py-2.5 text-[13px] text-slate-700 focus:bg-slate-50 focus:text-slate-900'
          >
            <Link href={PATH.SETTINGS}>
              <Settings className='h-[15px] w-[15px] text-slate-500' />
              Settings
            </Link>
          </DropdownMenuItem>

          <DropdownMenuItem
            className='cursor-pointer gap-2.5 rounded-none px-3.5 py-2.5 text-[13px] text-slate-700 focus:bg-slate-50 focus:text-slate-900'
            onSelect={handleLogout}
          >
            <LogOut className='h-[15px] w-[15px] text-slate-500' />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

const Sidebar: FC<ISidebarProps> = ({ onCloseSidebar }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState('');
  const [hasKeyboardFocus, setHasKeyboardFocus] = useState(false);
  const keyboardInteraction = useRef(true);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const workspaceContext = useWorkspace();
  const { user } = useAuth();
  const { setOpen: setCommandPaletteOpen } = useCommandPalette();
  const { shouldShowWorkspaceSwitcher } = useWorkspaceDetection();
  const pathname = usePathname() ?? '';
  const isSettingsPage = pathname.startsWith('/settings');
  const { expandedSidebar, isMobileMatches, sidebarMode, toggleSidebarMode, isHovering,
    setIsHovering, isWorkspaceSwitcherOpen } = useProtectedLayoutContext();

  useEffect(() => {
    // Track input outside the sidebar too: Tab can enter it, and portaled menus
    // restore focus to their trigger after either a click or a keyboard dismissal.
    const handlePointerDown = () => {
      keyboardInteraction.current = false;
      setHasKeyboardFocus(false);
    };
    const handleKeyDown = () => { keyboardInteraction.current = true; };
    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    getProfileAvatar().then((url) => { if (!cancelled) setAvatarUrl(url ?? ''); });
    return () => { cancelled = true; };
  }, []);

  const handleLogout = async () => {
    setIsLoading(true);
    await logout();
    setIsLoading(false);
  };
  const isVisuallyExpanded = isSettingsPage || expandedSidebar || isWorkspaceSwitcherOpen
    || (sidebarMode === 'auto-hide' && (isHovering || hasKeyboardFocus));
  const openCommands = () => { onCloseSidebar?.(); setCommandPaletteOpen(true); };

  return (
    <>
      <LoadingIndicator isLoading={isLoading} />
      <aside aria-label='Workspace navigation'
        className={clsx('mbn mbn-sidebar', isVisuallyExpanded && 'mbn-expanded',
          !isSettingsPage && !expandedSidebar && isVisuallyExpanded && 'mbn-floating')}
        onMouseEnter={(event) => { if (!isMobileMatches && sidebarMode === 'auto-hide' && event.currentTarget.contains(event.target as Node)) setIsHovering(true); }}
        onMouseLeave={() => setIsHovering(false)}
        onFocusCapture={(event) => { if (event.currentTarget.contains(event.target as Node)) setHasKeyboardFocus(keyboardInteraction.current); }}
        onBlurCapture={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setHasKeyboardFocus(false); }}
        // Include owned portals so Escape keeps their trigger visible for focus restoration.
        onKeyDownCapture={() => setHasKeyboardFocus(true)}
        onKeyDown={(event) => {
          if (event.key === '?' && event.currentTarget.contains(event.target as Node) && !(event.target as HTMLElement).closest('input,textarea,[contenteditable=true]')) {
            event.preventDefault(); event.stopPropagation(); setShortcutsOpen(true);
          }
        }}>
        <div className='mbn-sidebar-inner'>
          <div className='mbn-workspace-head'>
            <Link href='/' className='mbn-brand' aria-label='MarketBetter home'>
              <Image src='/images/logo/mb_small.png' width={48} height={28} alt='' />
            </Link>
            {!isSettingsPage && workspaceContext && shouldShowWorkspaceSwitcher ? (
              <div className='mbn-workspace-slot' hidden={!isVisuallyExpanded}>
                <WorkspaceSwitcher align='start' navigation />
              </div>
            ) : isVisuallyExpanded && <span className='mbn-workspace-name'>MarketBetter</span>}
            {!isMobileMatches && !isSettingsPage && isVisuallyExpanded && (
              <button type='button' className='mbn-icon-button' onClick={(event) => { toggleSidebarMode(); event.currentTarget.blur(); }}
                aria-label={sidebarMode === 'auto-hide' ? 'Keep sidebar expanded' : 'Auto-hide sidebar'}
                title={`${sidebarMode === 'auto-hide' ? 'Pin' : 'Auto-hide'} sidebar · Ctrl/⌘ \\`}>
                {sidebarMode === 'auto-hide' ? <PanelLeftOpen aria-hidden='true' /> : <PanelLeftClose aria-hidden='true' />}
              </button>
            )}
            <CloseButtonMobile onClick={onCloseSidebar} aria-label='Close navigation' />
          </div>
          <button type='button' className='mbn-search' onClick={openCommands} aria-label='Search and commands' title='Search and commands · Ctrl/⌘ K'>
            <Search aria-hidden='true' />
            {isVisuallyExpanded && <><span>Search and commands</span><kbd>⌘ K</kbd></>}
          </button>
          <div className='mbn-scroll'>
            {isSettingsPage ? (
              <>
                <Link href='/dashboard' className='mbn-nav-link mbn-back' onClick={onCloseSidebar}>
                  <ChevronLeft aria-hidden='true' /><span>Dashboard</span>
                </Link>
                <SettingsSidebarMenu isVisuallyExpanded={isVisuallyExpanded} onCloseSidebar={onCloseSidebar} />
              </>
            ) : workspaceContext?.isLoading && !workspaceContext.activeWorkspace ? (
              <div role='status' aria-label='Loading navigation' className='mbn-nav-skeleton'>
                {Array.from({ length: 8 }, (_, index) => <span key={index} className='mbn-skeleton' />)}
              </div>
            ) : <Menu onCloseSidebar={onCloseSidebar} isVisuallyExpanded={isVisuallyExpanded} />}
          </div>
          {!isMobileMatches && !isSettingsPage && isVisuallyExpanded && <SidebarCreditUsage />}
          <div className='mbn-sidebar-foot'>
            <button type='button' className='mbn-help' aria-label='Help and support' title='Help and support' onClick={openCommands}>
              <CircleHelp aria-hidden='true' />{isVisuallyExpanded && <span>Help &amp; support</span>}
            </button>
            {isVisuallyExpanded && <button type='button' className='mbn-icon-button' aria-label='Navigation shortcuts' title='Navigation shortcuts' onClick={() => setShortcutsOpen(true)}><Keyboard aria-hidden='true' /></button>}
            {isMobileMatches && <UserOptions avatarUrl={avatarUrl} user={user} handleLogout={handleLogout} />}
          </div>
        </div>
      </aside>
      <Dialog open={shortcutsOpen} onOpenChange={setShortcutsOpen}>
        <DialogContent className='mbn mbn-shortcuts'>
          <DialogTitle>Navigation shortcuts</DialogTitle>
          <DialogDescription>Page shortcuts remain available within each page.</DialogDescription>
          <dl>
            <div><dt>Ctrl / ⌘ K</dt><dd>Search and commands</dd></div>
            <div><dt>Ctrl / ⌘ \</dt><dd>Pin or auto-hide the sidebar on desktop</dd></div>
            <div><dt>Tab / Shift Tab</dt><dd>Move between links and controls</dd></div>
            <div><dt>Escape</dt><dd>Close a menu or dialog</dd></div>
            <div><dt>?</dt><dd>Show this guide while navigation has focus</dd></div>
          </dl>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Sidebar;
